import { useEffect, useRef, useState } from 'react'

import { TOOL_CALL_MIN_DISPLAY_DURATION_MS } from '../constants'

/**
 * Custom hook to manage cycling through tool calls with minimum display duration
 * @param {Array} toolCalls - Array of tool calls to cycle through
 * @param {boolean} isEnabled - Whether the feature is enabled
 * @returns {Object} - { displayedToolCall: Object|null }
 */
export const useToolCallCycling = (toolCalls = [], isEnabled = false) => {
  const [displayedToolCall, setDisplayedToolCall] = useState(null)
  const timeoutRef = useRef(null)
  const displayedToolCallIdsRef = useRef(new Set())
  const toolCallsQueueRef = useRef([])
  const displayStartTimeRef = useRef(null)
  const displayedToolCallRef = useRef(null)

  // Keep ref in sync with displayedToolCall state
  useEffect(() => {
    displayedToolCallRef.current = displayedToolCall
  }, [displayedToolCall])

  // Helper function to schedule next tool call from queue
  const scheduleNextToolCall = useRef(() => {
    if (toolCallsQueueRef.current.length > 0) {
      const nextToolCall = toolCallsQueueRef.current[0]
      toolCallsQueueRef.current = toolCallsQueueRef.current.slice(1)
      setDisplayedToolCall(nextToolCall)
      displayStartTimeRef.current = Date.now()

      // Schedule next tool call if there are more in queue
      if (toolCallsQueueRef.current.length > 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          scheduleNextToolCall.current()
        }, TOOL_CALL_MIN_DISPLAY_DURATION_MS)
      } else {
        timeoutRef.current = null
      }
    } else {
      timeoutRef.current = null
    }
  })

  // Reset state when feature is disabled
  useEffect(() => {
    if (!isEnabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setDisplayedToolCall(null)
      displayStartTimeRef.current = null
      toolCallsQueueRef.current = []
      displayedToolCallIdsRef.current.clear()
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isEnabled])

  // Process tool calls: reverse to show oldest first, and track which ones we've seen
  useEffect(() => {
    if (!isEnabled) {
      return () => {}
    }

    // If tool calls become empty, clear everything
    if (toolCalls.length === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setDisplayedToolCall(null)
      displayStartTimeRef.current = null
      toolCallsQueueRef.current = []
      displayedToolCallIdsRef.current.clear()
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }

    // Reverse tool calls to show oldest first (API returns most recent first)
    const reversedToolCalls = [...toolCalls].reverse()

    // Check if currently displayed tool call is still in the list
    // Use ref to get the latest value without triggering re-runs when displayedToolCall changes
    const currentDisplayedId = displayedToolCallRef.current?.id
    const isCurrentDisplayedStillValid = currentDisplayedId && reversedToolCalls.some((tc) => tc.id === currentDisplayedId)

    // If we have a displayed tool call that's still valid, preserve it and just add new ones to queue
    if (currentDisplayedId && isCurrentDisplayedStillValid) {
      // Ensure currently displayed tool call is marked as seen
      displayedToolCallIdsRef.current.add(currentDisplayedId)

      // Add new tool calls to queue that haven't been displayed yet
      const newToolCalls = reversedToolCalls.filter((tc) => !displayedToolCallIdsRef.current.has(tc.id))

      if (newToolCalls.length > 0) {
        // Add new tool calls to the queue
        toolCallsQueueRef.current = [...toolCallsQueueRef.current, ...newToolCalls]
        // Mark them as seen
        newToolCalls.forEach((tc) => displayedToolCallIdsRef.current.add(tc.id))
      }

      // Don't change the displayed tool call - keep showing current one
      // Ensure a timeout is scheduled to show the next tool call after minimum duration
      if (displayStartTimeRef.current && toolCallsQueueRef.current.length > 0) {
        const now = Date.now()
        const timeSinceDisplay = now - displayStartTimeRef.current
        const remainingTime = TOOL_CALL_MIN_DISPLAY_DURATION_MS - timeSinceDisplay

        if (remainingTime > 0) {
          // Still need to wait before showing next tool call
          // Always ensure a timeout is scheduled (clear and reschedule if needed)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
          timeoutRef.current = setTimeout(() => {
            scheduleNextToolCall.current()
          }, remainingTime)
        } else {
          // Minimum duration has passed, show next tool call immediately
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }
          scheduleNextToolCall.current()
        }
      } else if (toolCallsQueueRef.current.length > 0 && !displayStartTimeRef.current) {
        // We have tool calls in queue but no displayed one and no start time
        // This shouldn't happen, but ensure we show the first one
        scheduleNextToolCall.current()
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }
    // No displayed tool call or it's no longer valid
    const wasDisplayedToolCallCleared = displayedToolCallRef.current && !isCurrentDisplayedStillValid
    if (wasDisplayedToolCallCleared) {
      setDisplayedToolCall(null)
      displayedToolCallRef.current = null // Update ref immediately to avoid race condition
      displayStartTimeRef.current = null
      // Don't mark it as seen since it was cleared before completing minimum display duration
      // This allows it to be displayed again if it reappears
    }

    // Ensure currently displayed tool call is marked as seen (if any and still valid)
    // Only mark as seen if it's still in the list - if it was cleared, don't mark it
    if (currentDisplayedId && isCurrentDisplayedStillValid) {
      displayedToolCallIdsRef.current.add(currentDisplayedId)
    }

    // Add new tool calls to queue that haven't been displayed yet
    const newToolCalls = reversedToolCalls.filter((tc) => !displayedToolCallIdsRef.current.has(tc.id))

    if (newToolCalls.length > 0) {
      // Add new tool calls to the queue
      toolCallsQueueRef.current = [...toolCallsQueueRef.current, ...newToolCalls]
      // Mark them as seen
      newToolCalls.forEach((tc) => displayedToolCallIdsRef.current.add(tc.id))
    }

    // If we just cleared the displayed tool call and have items in queue, display the first one immediately
    if (wasDisplayedToolCallCleared && toolCallsQueueRef.current.length > 0) {
      scheduleNextToolCall.current()
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }

    // If no tool calls in queue and no displayed tool call, clear display
    if (toolCallsQueueRef.current.length === 0 && !displayedToolCallRef.current) {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }

    // If we have a displayed tool call, ensure timeout is scheduled for next tool call
    if (displayedToolCallRef.current && displayStartTimeRef.current && toolCallsQueueRef.current.length > 0) {
      const now = Date.now()
      const timeSinceDisplay = now - displayStartTimeRef.current
      const remainingTime = TOOL_CALL_MIN_DISPLAY_DURATION_MS - timeSinceDisplay

      if (remainingTime > 0) {
        // Still need to wait before showing next tool call
        // Always ensure a timeout is scheduled (clear and reschedule if needed)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          scheduleNextToolCall.current()
        }, remainingTime)
        // Don't change displayed tool call - keep showing current one
        return () => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
        }
      }
      // Minimum duration has passed, show next from queue immediately
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      scheduleNextToolCall.current()
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }

    // If no displayed tool call, show next from queue immediately
    if (!displayedToolCallRef.current) {
      if (toolCallsQueueRef.current.length > 0) {
        scheduleNextToolCall.current()
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [toolCalls, isEnabled])

  return { displayedToolCall }
}
